/**
 * IndividualIdentitySection — Identity block for the Individual person dialog.
 *
 * Port of legacy `PersonModal.IdentitySection` plus the §8 fix: customerType
 * is READ-ONLY (derivation-driven) within the Personal entity group. Officer
 * edits DOB / citizenship to change the derived type — no manual Entity Type
 * picker.
 *
 * Fields:
 *   - First name (editable)
 *   - Middle name (editable) — MAJOR GAP FIX per Bill's 2026-04-23 feedback;
 *     legacy PersonModal exposed this but the current demo wasn't wiring it
 *     through to Core Director Field 936.
 *   - Last name (editable)
 *   - Suffix (Jr/Sr/II/III/IV)
 *   - SSN/TIN (numeric, max 9 chars, formatted via formatSsn preview)
 *   - Tax ID Code / Field 92 (S = SSN, N = None required; cohort-aware default —
 *     US/PR → 'S', NRA → 'N'. 'Forn' shown read-only when set by the NRA
 *     citizenship cohort. EIN 'E' is business-only, not offered here.)
 *   - Date of Birth (date input)
 *   - Gender (blank/Male/Female)
 *   - Entity Type chip (READ-ONLY; shows derived type + source tooltip)
 *   - Insider Code (0-9)
 *   - Backup Withholding (checkbox)
 *
 * Auto-minor banner: when the derivation engine flags Minor for a new person
 * based on DOB, render the one-line blue info note.
 */

import { useEffect } from 'react';
import { DateTextInput, Pill } from '@/components/primitives';
import { formatSsn } from '@/utils/ssn';
import { useConfig } from '@/contexts/ConfigContext';
import { readTestIdentitySettings, testIdentityViolation, testModeDraftViolation } from '@/utils/testIdentity';
import { useAuth } from '@/contexts/AuthContext';
import type { RaoPerson, RaoTinCode } from '../../../types';
import { ageFromDob } from '../../../utils/age';
import type { DerivationResult } from './useIndividualDerivation';
import {
  NAME_SUFFIXES,
  INSIDER_CODES,
} from './individual-config';

// -----------------------------------------------------------------------------
// Shared leaf styles (lifted from legacy PersonModal for visual consistency)
// -----------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const readOnlyStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--field-readonly-bg, #f5f5f5)',
  color: 'var(--ink-700)',
};

function changedFieldStyle(base: React.CSSProperties, changed: boolean): React.CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface IndividualIdentitySectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  mode: 'new' | 'existing';
  isChanged?: (field: string) => boolean;
  derivation: DerivationResult;
  readOnly?: boolean;
  /**
   * E1 (Bill 2026-06-03): fired on SSN-field blur once a COMPLETE 9-digit SSN is
   * entered, so the dup-CIF check can run early (when CIF info is entered) rather
   * than at the final Add. Parent decides whether to run the silent CustSrch.
   */
  onSsnComplete?: (ssn: string) => void;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function derivationExplain(d: DerivationResult): string {
  switch (d.derivedFrom) {
    case 'dob':         return 'derived from DOB';
    case 'citizenship': return 'derived from citizenship';
    case 'baseline':    return 'new person baseline';
    default:            return 'derived';
  }
}

function entityTypePillVariant(type: string): 'info' | 'warn' | 'muted' {
  // Pill variants available in v3 primitives: 'ok' | 'warn' | 'err' | 'info' | 'muted'
  if (type === 'Minor') return 'warn';
  if (type === 'NonCitizen') return 'warn';
  return 'info';
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function IndividualIdentitySection({
  person,
  onChange,
  mode,
  isChanged,
  derivation,
  readOnly = false,
  onSsnComplete,
}: IndividualIdentitySectionProps): JSX.Element {
  const isExisting = mode === 'existing';
  const ic = isChanged ?? ((_: string) => false);
  // Test-identity rules (Bill 2026-09-15): the last name "Test" and a 666
  // SSN/TIN/ATIN travel together. Shown as the officer types; the save gate,
  // the Review gate and the server enforce the same rule.
  const { config } = useConfig();
  const { user } = useAuth();
  const identitySettings = readTestIdentitySettings(config.systemSettings);
  // Test mode (Bill 2026-10-07): the stricter draft rule mirrors the server's save gate.
  const testModeOn = user?.testMode?.active === true;
  const identityMessage = readOnly
    ? undefined
    : testModeOn
      ? testModeDraftViolation(person, identitySettings)
      : testIdentityViolation(person, identitySettings);

  // Cohort-aware Tax ID Code (Field 92) default — No-TIN / Field-92 control
  // (Bill ruling #4, 2026-06-12). US Citizen / Permanent Resident default 'S'
  // (SSN); Non-Resident Alien defaults 'N' (None required) — NRAs commonly have
  // no US SSN. 'Forn' (foreign-tax-resident, set by the citizenship section when
  // syncCustomerType is on) is NEVER clobbered here — it is preserved/displayed
  // read-only on the selector below.
  const cohortTinDefault: RaoTinCode =
    person.citizenshipStatus === 'Non-Resident Alien' ? 'N' : 'S';

  // 'Forn' is set by the citizenship cohort (NRA) when syncCustomerType is on.
  // When present, the Field-92 selector is shown read-only as 'Forn' rather
  // than offering S/N (Bill ruling #4 — never clobber a foreign-tax-resident).
  const tinIsForeign = person.tinCode === 'Forn';
  // The editable selector resolves an unset code to the cohort default so the
  // control always reflects the value that will be committed.
  const tinSelectValue: RaoTinCode = person.tinCode ?? cohortTinDefault;
  // Field 92 = N (None required): no US SSN/TIN on file → the SSN number field
  // becomes optional and a plain-language note is shown.
  const tinIsNone = tinSelectValue === 'N';
  // No US tax ID at all (N or Forn): backup withholding does not apply (Bill 2026-10-14).
  const noTaxId = tinIsNone || tinIsForeign;

  // Keep person.customerType synced with the derivation engine. The type is
  // not editable via this section, but it IS the value that persists into the
  // cart envelope + CustAdd/CustMod payload. So we push the derived value
  // into the parent on mount and whenever derivation changes.
  useEffect(() => {
    if (person.customerType !== derivation.customerType) {
      onChange({ customerType: derivation.customerType as RaoPerson['customerType'] });
    }
    // Seed the cohort-aware Field-92 default ONCE, only when tinCode is unset
    // and we're capturing a new person. We never overwrite an FSR choice and
    // never clobber a pre-set 'Forn' — the previous force-to-'S' lock is gone
    // (Bill ruling #4: the FSR must be able to set N — None required).
    if (mode === 'new' && (person.tinCode === undefined || person.tinCode === null)) {
      onChange({ tinCode: cohortTinDefault });
    }
  }, [derivation.customerType, person.customerType, person.tinCode, mode, cohortTinDefault, onChange]);

  // No tax ID → backup withholding can't be on (Bill 2026-10-14). Clears a flag set earlier.
  useEffect(() => {
    if (!readOnly && (person.tinCode === 'N' || person.tinCode === 'Forn') && person.backupWithholding) {
      onChange({ backupWithholding: false });
    }
  }, [person.tinCode, person.backupWithholding, readOnly, onChange]);

  // DOB entry (Collette Cruse life-ring report 2026-10-16; Bill approved): a typed
  // MM/DD/YYYY field replaces the browser date control, which would not take
  // number-keypad typing. Partial entry is still held locally (Bill 2026-07-13
  // Item 8) - DateTextInput reports only a complete, real date (ISO) or '' - and
  // a legacy non-ISO stored value still displays (lenient hydration via jxDates).

  const age = ageFromDob(person.dob);
  const showAutoMinorBanner =
    mode === 'new' &&
    derivation.customerType === 'Minor' &&
    derivation.derivedFrom === 'dob';

  const dobField = (
      <div>
        <label style={labelStyle} htmlFor="rao-ind-dob">Date of birth</label>
        <DateTextInput
          id="rao-ind-dob"
          value={person.dob ?? ''}
          onChange={(iso) => onChange({ dob: iso })}
          /* DOB is editable for existing CIFs too (Bill 2026-07-17): a wrong
             birth date on file must be correctable from Add Existing / Edit CIF,
             not just at first capture. Only the general read-only (no edit grant)
             still locks it. */
          readOnly={readOnly}
          style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('dob'))}
          title="Date of birth"
        />
        {age !== null && (
          <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>
            Age: <strong>{age}</strong>
            {age < 18 && (
              <span style={{ marginLeft: 6 }}>
                <Pill variant="warn">Minor</Pill>
              </span>
            )}
          </div>
        )}
      </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Read-only entity type chip — §8 EntityTypePicker-for-Personal-group */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          background: 'var(--info-bg)',
          border: '1px solid var(--info-line)',
          borderRadius: 6,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-700)' }}>
          Entity Type:
        </span>
        <Pill variant={entityTypePillVariant(derivation.customerType)}>
          {derivation.customerType}
        </Pill>
        <span
          style={{ fontSize: 11, color: 'var(--ink-600)' }}
          title={`Auto-derived: ${derivationExplain(derivation)}`}
        >
          ({derivationExplain(derivation)})
        </span>
        {derivation.promotedFrom && (
          <span style={{ fontSize: 11, color: 'var(--pcb-blue)', marginLeft: 'auto' }}>
            updated from &lsquo;{derivation.promotedFrom}&rsquo;
          </span>
        )}
      </div>

      {/* Auto-minor banner */}
      {showAutoMinorBanner && (
        <div
          style={{
            padding: '8px 12px',
            background: 'var(--info-bg)',
            border: '1px solid var(--pcb-blue)',
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--ink-900)',
          }}
        >
          Under 18 detected — entity type auto-set to <strong>Minor</strong>.
        </div>
      )}

      {/* Supervisor-approval gate */}
      {derivation.requiresSupervisorApproval && (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            background: 'var(--warn-bg)',
            border: '1px solid var(--warn-line)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--ink-900)',
          }}
        >
          <strong>Supervisor approval required.</strong> Core Director stored this
          CIF as &lsquo;{derivation.promotedFrom}&rsquo; but current data derives to&nbsp;
          &lsquo;{derivation.customerType}&rsquo;. A supervisor must approve the promotion
          before modifications can be saved.
        </div>
      )}

      {/* Name row: first / middle / last / suffix */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 100px',
          gap: 12,
        }}
      >
        <div>
          <label style={labelStyle} htmlFor="rao-ind-first">First name</label>
          <input
            id="rao-ind-first"
            type="text"
            value={person.firstName ?? ''}
            onChange={(e) => onChange({ firstName: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('firstName'))}
            title="First name"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-ind-middle">Middle initial</label>
          <input
            id="rao-ind-middle"
            type="text"
            value={person.middleName ?? ''}
            onChange={(e) => onChange({ middleName: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('middleName'))}
            title="Middle initial"
            maxLength={1}
            placeholder="Optional"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-ind-last">Last name</label>
          <input
            id="rao-ind-last"
            type="text"
            value={person.lastName ?? ''}
            onChange={(e) => onChange({ lastName: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('lastName'))}
            title="Last name"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-ind-suffix">Suffix</label>
          {/*
            Combo box: native <input list=…> + <datalist> renders a
            dropdown of canonical suffixes (Jr / Sr / II-IV) AND allows
            freeform typing for anything else (PhD, MD, Esq, etc.).
            Backed by Core Director field 937 (max 10 chars).
          */}
          <input
            id="rao-ind-suffix"
            list="rao-ind-suffix-options"
            type="text"
            value={person.suffix ?? ''}
            onChange={(e) => onChange({ suffix: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('suffix'))}
            title="Suffix (select or type — max 10 characters, Core Director field 937)"
            maxLength={10}
            placeholder="— / Jr / Sr / III / …"
            autoComplete="off"
          />
          <datalist id="rao-ind-suffix-options">
            {NAME_SUFFIXES
              .filter((s) => s.value !== '')
              .map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
          </datalist>
        </div>
      </div>

      {testModeOn && !readOnly && (
        // Test mode (Bill 2026-10-07): helper text. The server refuses anything else.
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            background: 'var(--pcb-purple-soft-bg)',
            color: 'var(--pcb-purple-fg)',
            fontSize: 13,
          }}
        >
          Test mode: use the last name &quot;{identitySettings.lastName}&quot; with an SSN/TIN/ATIN starting
          with {identitySettings.cifPrefix}, or leave the tax ID blank (Core Director then generates a{' '}
          {identitySettings.generatedCifPrefix} CIF). Real customers can&apos;t be added.
        </div>
      )}

      {identityMessage && (
        <div
          role="alert"
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            background: 'var(--err-bg)',
            color: 'var(--pcb-red, #b10505)',
            fontSize: 13,
          }}
        >
          {identityMessage}
        </div>
      )}

      {/* DOB / SSN / Tax ID code row (Bill 2026-10-14: DOB first — the no-tax-ID proof searches Core
          by name + date of birth, so the date comes before the SSN choice). */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 170px', gap: 12 }}>
        {dobField}
        <div>
          <label style={labelStyle} htmlFor="rao-ind-ssn">
            SSN / TIN / ATIN{' '}
            {!tinIsNone && person.taxId && person.taxId.length === 9 && (
              <span style={{ color: 'var(--ink-600)', fontWeight: 400 }}>
                ({formatSsn(person.taxId)})
              </span>
            )}
          </label>
          <input
            id="rao-ind-ssn"
            type="text"
            value={tinIsNone ? '' : (person.taxId ?? '')}
            onChange={(e) =>
              onChange({ taxId: e.currentTarget.value.replace(/\D/g, '').slice(0, 9) })
            }
            onBlur={(e) => {
              // E1 — fire the early dup-CIF check once a complete 9-digit tax id
              // is entered (blur = discrete commit point; no per-keystroke spam).
              const digits = e.currentTarget.value.replace(/\D/g, '');
              if (digits.length === 9) onSsnComplete?.(digits);
            }}
            readOnly={isExisting || readOnly || tinIsNone}
            aria-required={!tinIsNone}
            /* NO maxLength — the DOM truncates a dashed paste ("###-##-####",
               11 chars) to its first 9 chars BEFORE onChange can strip the
               dashes, silently dropping the last 2 digits (Bill 2026-07-13
               Item 10). onChange's replace(/\D/g,'').slice(0,9) is the cap. */
            style={changedFieldStyle(isExisting || readOnly || tinIsNone ? readOnlyStyle : inputStyle, ic('taxId'))}
            placeholder="123456789"
            title="SSN or TIN"
          />
          {/* Bill 2026-10-14: the no-tax-ID choice is a checkbox; the Field 92 code follows it.
              Ticking it sets N and clears the number; unticking returns to S. A foreign tax
              resident (Forn) is set by citizenship and shows here ticked and locked. */}
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 6, color: 'var(--ink-800)',
                     cursor: isExisting || readOnly || tinIsForeign ? 'default' : 'pointer' }}
          >
            <input
              type="checkbox"
              checked={tinIsNone || tinIsForeign}
              disabled={isExisting || readOnly || tinIsForeign}
              onChange={(e) =>
                onChange(e.currentTarget.checked
                  ? { tinCode: 'N', taxId: '', backupWithholding: false }
                  : { tinCode: 'S' })
              }
            />
            No SSN / TIN / ATIN
          </label>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-ind-tin">Tax ID code (F92)</label>
          {/* Read-only: follows the checkbox (or the citizenship cohort for Forn). */}
          <select
            id="rao-ind-tin"
            value={tinIsForeign ? 'Forn' : tinSelectValue}
            disabled
            style={readOnlyStyle}
            title="Tax ID code, Core Director Field 92. Set by the No SSN / TIN / ATIN box (or citizenship for a foreign tax ID)."
          >
            <option value="S">S · SSN</option>
            <option value="N">N · No tax ID number</option>
            <option value="Forn">Forn · Foreign tax ID</option>
          </select>
        </div>
      </div>

      {/* No-TIN (Field 92 = N) plain-language note — Bill ruling #4 (2026-06-12).
          The W-9 follow-up itself is created by the BE lane; we only state it. */}
      {tinIsNone && (
        <div
          style={{
            padding: '8px 12px',
            background: 'var(--info-bg)',
            border: '1px solid var(--info-line)',
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--ink-900)',
          }}
        >
          No SSN / TIN / ATIN. Core Director is searched by name and date of birth first; if
          there&apos;s no match, the customer gets a bank-assigned customer number, recorded
          with Tax ID code N. If this becomes an interest-bearing account, a W-9 follow-up
          is created automatically.
        </div>
      )}

      {/* Gender / Insider Code row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-ind-gender">Gender</label>
          <select
            id="rao-ind-gender"
            value={person.gender ?? ''}
            onChange={(e) => onChange({ gender: e.currentTarget.value as RaoPerson['gender'] })}
            disabled={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('gender'))}
            title="Gender"
          >
            <option value="">Unknown</option>
            <option value="Male">M · Male</option>
            <option value="Female">F · Female</option>
          </select>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-ind-insider">Insider code</label>
          <select
            id="rao-ind-insider"
            value={person.insiderCode ?? '0'}
            onChange={(e) => onChange({ insiderCode: e.currentTarget.value })}
            disabled={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('insiderCode'))}
            title="Insider code"
          >
            {INSIDER_CODES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Backup withholding */}
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 13,
          color: 'var(--ink-900)',
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={!noTaxId && !!person.backupWithholding}
          onChange={(e) => onChange({ backupWithholding: e.currentTarget.checked })}
          disabled={readOnly || noTaxId}
        />
        Backup withholding (Field N=Yes / E=No)
        {noTaxId && (
          <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>
            · not applicable without an SSN / TIN / ATIN
          </span>
        )}
      </label>
    </div>
  );
}
